import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams, useLocation } from "react-router-dom";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ComparePage from "./pages/ComparePage";
import NotFoundPage from "./pages/NotFoundPage";
import PlayerPage2 from "./pages/PlayerPages2/PlayerPage2";
import SteamCallbackPage from "./pages/SteamCallbackPage";
import { AuthProvider } from "./context/AuthContext";
import EditPlayerPage from "./pages/PlayerPages/EditPlayerPage";
import FreeAgentPage2 from "./pages/FreeAgentPage2/FreeAgentPage2";
import WrappedPage from "./pages/WrappedPage/WrappedPage";
import WrappedNudge from "./components/WrappedNudge";
import ImpersonationBar from "./components/ImpersonationBar";
import LookingForPlayersPage2 from "./pages/LookingForPlayers2/LookingForPlayersPage2";
import FantasyPage2 from "./pages/FantasyPage2/FantasyPage2";
import DonationsPage2 from "./pages/DonationsPage2/DonationsPage2";
import ViewFantasyTeamPage2 from "./pages/FantasyPage2/ViewFantasyTeamPage2";
import ViewAllTeamsPage2 from "./pages/FantasyPage2/ViewAllTeamsPage2";
import TeamPageV2 from "./pages/TeamPages/TeamPageV2";
import LeaderboardPage from "./pages/LeaderboardPage/LeaderboardPage";
import EditTeamPage from "./pages/TeamPages/EditTeamPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import MatchPage2 from './pages/MatchPage2/MatchPage2';
import StandingPage from "./pages/Standings/StandingPage";
import CacheBuster from 'react-cache-buster';
import Loading from "./components/Loading";
import packageInfo from '../package.json';
import MatchesPage from "./pages/MatchesPage";
import CreateFantasyPage from "./pages/FantasyPage/CreateFantasyTeamPage";
import FantasyGuidePage from "./pages/FantasyPage/FantasyGuidePage";
import Rating3Page from "./pages/Rating3Page";
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/buttons.css';
import PreMatchPage2 from './pages/PreMatchPage2/PreMatchPage2';
import PreMatch2Prep from './pages/PreMatchPage2/PreMatch2Prep';
import TeamsUsingPlayerPage from "./pages/FantasyPage/TeamsUsingPlayerPage";
import PredictStandingsPage2 from "./pages/Standings/PredictStandingsPage2";
import UserPredictionsPage2 from "./pages/Standings/UserPredictionsPage2";
import PredictionLeaderboardPage2 from "./pages/Standings/PredictionLeaderboardPage2";
import { useEffect } from "react";
import UploadDemoPage from "./pages/UserDemos/UploadDemoPage";
import MyDemosPage from "./pages/UserDemos/MyDemosPage";
import DemoViewerPage from "./pages/UserDemos/DemoViewerPage";
import LiveMatchPage from "./pages/LivePage/LiveMatchPage";
import ContentTeamPage2 from "./pages/Standings/ContentTeamPage2";
import { CONTENT_TEAMS } from "./config/contentTeams";
import PowerStriklePage from "./pages/PowerStriklePage";
import RatingDuelPage from "./pages/RatingDuelPage";
import LogoPuzzlePage from "./pages/LogoPuzzlePage";
import TeamRosterPage from "./pages/TeamRosterPage";
import WhosThatProPage from "./pages/WhosThatProPage";
import GamesHubPage from "./pages/GamesHubPage";
import GamesLeaderboardPage from "./pages/GamesLeaderboardPage";
import StatsViewPage from "./pages/StatsView/StatsViewPage";
import StatsViewRating21Page from "./pages/StatsView/StatsViewRating21Page";
import StatsViewRating30Page from "./pages/StatsView/StatsViewRating30Page";
import StatsViewSwingPage from "./pages/StatsView/StatsViewSwingPage";
import StatsViewCTPage from "./pages/StatsView/StatsViewCTPage";
import StatsViewTPage from "./pages/StatsView/StatsViewTPage";
import StatsViewMultiPage from "./pages/StatsView/StatsViewMultiPage";
import LeagueStatsPage from "./pages/LeagueStatsPage";
import LeagueStatsHardstuckPage from "./pages/LeagueStatsHardstuckPage";
import LeagueStatsClimbersPage from "./pages/LeagueStatsClimbersPage";
import LeagueStatsTalentsPage from "./pages/LeagueStatsTalentsPage";
import TransfersPage from "./pages/TransfersPage/TransfersPage";
import TopClipsPage from "./pages/ClipsPage/TopClipsPage";
import ClipsOverlay from "./pages/ClipsPage/ClipsOverlay";
import CameraSenderPage from "./pages/CameraPage/CameraSenderPage";
import TierListPage from "./pages/TierListPage/TierListPage";
import ContentHubPage from "./pages/ContentHubPage";
import PlayerScoutPage from "./pages/PlayerScoutPage";
import HomePageV2 from "./pages/HomePageV2/HomePageV2";
import AnalysisFilterPage from "./pages/AnalysisPage/AnalysisFilterPage";
import AnalysisViewPage from "./pages/AnalysisPage/AnalysisViewPage";
import TournamentAdminPage from "./pages/TournamentAdminPage/TournamentAdminPage";
import SiteBannerAdminPage from "./pages/SiteBannerAdminPage/SiteBannerAdminPage";
import DonatorPage from "./pages/DonatorPage/DonatorPage";
import AdminUsersPage from "./pages/AdminUsersPage/AdminUsersPage";
import AdminDonatorsPage from "./pages/AdminDonatorsPage/AdminDonatorsPage";
import AdminHubPage from "./pages/AdminHubPage/AdminHubPage";
import AdminUploadedDemosPage from "./pages/AdminUploadedDemosPage/AdminUploadedDemosPage";
import AdminOverPassUsagePage from "./pages/AdminOverPassUsagePage/AdminOverPassUsagePage";
import AdminRecomputePage from "./pages/AdminRecomputePage/AdminRecomputePage";
import UnsignedGraphicsPage from "./pages/UnsignedGraphicsPage/UnsignedGraphicsPage";
import ApiKeyAdminPage from "./pages/ApiKeyAdminPage/ApiKeyAdminPage";
import ApiDocsPage from "./pages/ApiDocsPage/ApiDocsPage";


/**
 * Redirects a retired dark-launch URL to its canonical one, carrying the route param (if any), the
 * query and the hash across (/match2/123 -> /match/123, /prematch2/123/prep -> /prematch/123/prep).
 * The redesigned pages were dark-launched on "2" URLs and are now the default, but those URLs are
 * already out in Discord posts and shared links, so they redirect rather than 404.
 */
const LegacyRedirect = ({ to, param, suffix = "" }: { to: string; param?: string; suffix?: string }) => {
    const params = useParams();
    const value = param ? params[param] : undefined;
    const { search, hash } = useLocation();
    return <Navigate to={`${value ? `${to}/${value}${suffix}` : to}${search}${hash}`} replace />;
};

/** /fantasy2/view/<anything> -> /fantasy/view/<anything>, for both the all-teams and single-team URLs. */
const LegacyFantasyViewRedirect = () => {
    const rest = useParams()["*"] ?? "";
    const { search, hash } = useLocation();
    return <Navigate to={`${rest ? `/fantasy/view/${rest}` : "/fantasy"}${search}${hash}`} replace />;
};

function App() {

  const isProduction = process.env.NODE_ENV === 'production';
  console.log("Production:", isProduction);
  console.log("Version:", packageInfo.version);
  return (
    <div className="App">
      <CacheBuster
        currentVersion={packageInfo.version}
        isEnabled={isProduction} //If false, the library is disabled.
        isVerboseMode={false} //If true, the library writes verbose logs to console.
        loadingComponent={<Loading />} //If not pass, nothing appears at the time of new version check.
        metaFileDirectory={'.'} //If public assets are hosted somewhere other than root on your server.
      >
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Overlay routes - no Header/Footer */}
              <Route path="/clips/overlay" element={<ClipsOverlay />} />
              {/* PowerHuds player-camera sender page. Deliberately unlisted:
                  no nav/homepage link anywhere — players reach it only via
                  the tokenized URL from PowerHuds. Chrome-less on purpose. */}
              <Route path="/camera/:token" element={<CameraSenderPage />} />

{/* All other routes - with Header/Footer via layout */}
              <Route element={
                <div className="background-color">
                  <Header />
                  <div className="container min-vh-100" style={{ paddingBottom: '60px' }}>
                    <Outlet />
                  </div>
                  <Footer />
                  {/* Season Wrapped launch nudge — self-expires on Constants.WRAPPED_PROMO_LAST_DAY */}
                  <WrappedNudge />
                  {/* Admin "view as user" banner — shows only while impersonating */}
                  <ImpersonationBar />
                </div>
              }>
                {["/", "/home", "/index"].map((path, index) =>
                  <Route path={path} element={<HomePageV2 />} key={index} />
                )}
                {/* The pre-redesign homepage is gone; keep the URL from 404ing. */}
                <Route path="/home-old" element={<Navigate to="/" replace />} />
                <Route path="/player/:steamId/edit" element={<EditPlayerPage />} />
                <Route path="/player/:steamId" element={<PlayerPage2 />} />
                {/* Legacy dark-launch URL — kept so shared /player2 links keep resolving. */}
                <Route path="/player2/:steamId" element={<LegacyRedirect to="/player" param="steamId" />} />
                <Route path="/team/:teamId" element={<TeamPageV2 />} />
                <Route path="/team/:teamId/edit" element={<EditTeamPage />} />
                <Route path="/matches" element={<MatchesPage />} />
                <Route path="/standings" element={<StandingPage />} />
                <Route path="/standing/:season/:division" element={<StandingPage />} />
                <Route path="/lft" element={<FreeAgentPage2 />} />
                <Route path="/lft2" element={<Navigate to="/lft" replace />} />
                {/* Season Wrapped, dark-launched for beta users + admins until season end (tasks/wrapped-design.md) */}
                <Route path="/wrapped" element={<WrappedPage />} />
                <Route path="/wrapped/:season/:steamId" element={<WrappedPage />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/steam/callback" element={<SteamCallbackPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/match/:matchId" element={<MatchPage2 />} />
                {/* Legacy dark-launch URL — kept so shared /match2 links keep resolving. */}
                <Route path="/match2/:matchId" element={<LegacyRedirect to="/match" param="matchId" />} />
                <Route path="/prematch/:matchId" element={<PreMatchPage2 />} />
                <Route path="/prematch/:matchId/prep" element={<PreMatch2Prep />} />
                {/* Legacy dark-launch URLs — kept so shared /prematch2 links keep resolving. */}
                <Route path="/prematch2/:matchId" element={<LegacyRedirect to="/prematch" param="matchId" />} />
                <Route path="/prematch2/:matchId/prep" element={<LegacyRedirect to="/prematch" param="matchId" suffix="/prep" />} />
                <Route path="/fantasy" element={<FantasyPage2 />} />
                <Route path="/fantasy/view/:steamID/:season/:division" element={<ViewFantasyTeamPage2 />} />
                <Route path="/fantasy/view/:steamID/:season" element={<ViewAllTeamsPage2 />} />
                {/* Legacy dark-launch URLs — kept so shared /fantasy2 links keep resolving. */}
                <Route path="/fantasy2" element={<Navigate to="/fantasy" replace />} />
                <Route path="/fantasy2/view/*" element={<LegacyFantasyViewRedirect />} />
                <Route path="/fantasy/create" element={<CreateFantasyPage />} />
                <Route path="/fantasy/guide" element={<FantasyGuidePage />} />
                {/* Rating 3.0 explainer — linked from the launch post and every R3 tooltip. */}
                <Route path="/rating3" element={<Rating3Page />} />
                <Route path="/donations" element={<DonationsPage2 />} />
                <Route path="/donations2" element={<Navigate to="/donations" replace />} />
                <Route path="/lfp" element={<LookingForPlayersPage2 />} />
                <Route path="/lfp2" element={<Navigate to="/lfp" replace />} />
                <Route path="/transfers" element={<TransfersPage />} />
                <Route path="/clips" element={<TopClipsPage />} />
                <Route path="/fantasy/teams-using-player/:steamID/:season" element={<TeamsUsingPlayerPage />} />
                {/* Absorbed into the Fantasy hub's "picks" / "single" tabs, which read the same
                    services. The standalone pages are gone; their URLs land on the hub. */}
                <Route path="/fantasy/detailed-picks/:season" element={<Navigate to="/fantasy" replace />} />
                <Route path="/fantasy/best-picks/:season" element={<Navigate to="/fantasy" replace />} />
                <Route path="/fantasy/best-picks" element={<Navigate to="/fantasy" replace />} />
                <Route path="/standings/predict" element={<PredictStandingsPage2/>} />
                <Route path="/standings/predict/:steamId" element={<UserPredictionsPage2/>} />
                <Route path="/standings/leaderboard" element={<PredictionLeaderboardPage2/>} />
                {/* Legacy dark-launch URLs — kept so shared predict2/leaderboard2 links keep resolving. */}
                <Route path="/standings/predict2" element={<LegacyRedirect to="/standings/predict" />} />
                <Route path="/standings/predict2/:steamId" element={<LegacyRedirect to="/standings/predict" param="steamId" />} />
                <Route path="/standings/leaderboard2" element={<LegacyRedirect to="/standings/leaderboard" />} />
                {/* Live maps are a tab on the Kampe page; /live/:matchId is one map's scoreboard. */}
                <Route path="/live" element={<Navigate to="/matches?tab=live" replace />} />
                <Route path="/live/:matchId" element={<LiveMatchPage />} />
                {CONTENT_TEAMS.map(team => (
                  <Route key={team.teamId} path={team.route} element={<ContentTeamPage2 config={team} />} />
                ))}
                {/* The ...2 URLs the rebuilt podcaster pages were shared under before they took over the main route */}
                {CONTENT_TEAMS.map(team => (
                  <Route key={`${team.teamId}-2`} path={`${team.route}2`} element={<ContentTeamPage2 config={team} />} />
                ))}
                <Route path="/powerstrikle" element={<PowerStriklePage />} />
                <Route path="/rating-duel" element={<RatingDuelPage />} />
                <Route path="/logo-puzzle" element={<LogoPuzzlePage />} />
                <Route path="/team-roster" element={<TeamRosterPage />} />
                <Route path="/whos-that-pro" element={<WhosThatProPage />} />
                <Route path="/games" element={<GamesHubPage />} />
                <Route path="/games/leaderboard" element={<GamesLeaderboardPage />} />
                <Route path="/statsview" element={<StatsViewPage />} />
                <Route path="/statsview-rating21" element={<StatsViewRating21Page />} />
                <Route path="/statsview-rating30" element={<StatsViewRating30Page />} />
                <Route path="/statsview-swing" element={<StatsViewSwingPage />} />
                <Route path="/statsview-ct" element={<StatsViewCTPage />} />
                <Route path="/statsview-t" element={<StatsViewTPage />} />
                <Route path="/statsview-multi" element={<StatsViewMultiPage />} />
                <Route path="/leaguestats" element={<LeagueStatsPage />} />
                <Route path="/leaguestats/hardstuck" element={<LeagueStatsHardstuckPage />} />
                <Route path="/leaguestats/climbers" element={<LeagueStatsClimbersPage />} />
                <Route path="/leaguestats/talents" element={<LeagueStatsTalentsPage />} />
                <Route path="/content" element={<ContentHubPage />} />
                <Route path="/scout" element={<PlayerScoutPage />} />
                <Route path="/premium" element={<DonatorPage />} />
                <Route path="/admin" element={<AdminHubPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/donators" element={<AdminDonatorsPage />} />
                <Route path="/admin/uploaded-demos" element={<AdminUploadedDemosPage />} />
                <Route path="/analyse" element={<AnalysisFilterPage />} />
                <Route path="/analyse/view" element={<AnalysisViewPage />} />
                <Route path="/tierlist" element={<TierListPage key="create" />} />
                <Route path="/tierlist/:id" element={<TierListPage key="view" />} />
                <Route path="/tierlist/:id/rank" element={<TierListPage key="rank" rank />} />
                <Route path="/upload-demo" element={<UploadDemoPage />} />
                <Route path="/my-demos" element={<MyDemosPage />} />
                <Route path="/demo/:uploadId" element={<DemoViewerPage />} />
                <Route path="/shared-demo/:token" element={<DemoViewerPage />} />
                <Route path="/admin/tournaments" element={<TournamentAdminPage />} />
                <Route path="/admin/banner" element={<SiteBannerAdminPage />} />
                <Route path="/admin/overpass-usage" element={<AdminOverPassUsagePage />} />
                <Route path="/admin/recompute" element={<AdminRecomputePage />} />
                <Route path="/admin/unsigned-graphics" element={<UnsignedGraphicsPage />} />
                <Route path="/admin/api-keys" element={<ApiKeyAdminPage />} />
                {/* Public, interactive docs for the /api/public/v1 API (casters/streamers) */}
                <Route path="/api" element={<ApiDocsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </CacheBuster>
    </div>
  );
}

export default App;
